<template>
    <div class="text-slate-800">
        <div
            class="bg-white p-6 flex flex-col gap-4 border-b border-x-0 border-t-0 border-b-slate-100 border-solid">

            <div>
                <div class="text-2xl font-extrabold">
                    <slot name="header">Select Items</slot>
                </div>
                <div class="text-sm text-slate-500">
                    <slot name="subheader">You can select multiple items.</slot>
                </div>
            </div>

            <div>
                <vx-tabs v-model="tab"
                    size="lg">
                    <vx-tab value="all">All</vx-tab>
                    <vx-tab value="selected">Selected ({{ selected.length }})</vx-tab>
                </vx-tabs>
            </div>
            <div>
                <vx-input ref="searchEl"
                    v-model="search"
                    @keydown.enter="handleEnter"
                    size="lg"
                    data-test="search-input">
                    <template v-slot:right>
                        <font-awesome-icon widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
        </div>

        <div class="bg-slate p-6 lg:px-12 lg:py-10">
            <div v-if="tab === 'all'">
                <div class="flex justify-between items-center pl-4 pb-4">
                    <div v-if="filteredOptions.length > 1"
                        @click="toggleAll"
                        role="button"
                        tabindex="0"
                        data-test="select-all-button"
                        class="flex items-center gap-2">
                            <vx-checkbox :modelValue="selectAll"></vx-checkbox>
                            <span class="text-sm">Select All</span>
                    </div>

                    <slot name="select-all-right"/>
                </div>

                <div v-if="filteredOptions.length > 0"
                    class="flex flex-col gap-2">
                    <page-select-item v-for="item in filteredOptions"
                        :key="'all_' + props.getOptionKey(item)"
                        :label="props.getOptionLabel(item)"
                        @toggle="toggle(item)"
                        :selected="isSelected(item)">
                        <slot name="option"
                            :item="item"
                            :selected="isSelected(item)"></slot>
                    </page-select-item>
                </div>
                <div v-else
                    class="flex p-8 items-center justify-center flex-col gap-3">
                    <template v-if="props.loading">
                        <vx-icon-loading spin
                            class="w-6 h-6"></vx-icon-loading>
                        <span class="font-normal text-lg">Loading...</span>
                    </template>
                    <template v-else>
                        <font-awesome-icon widthAuto :icon="faSquareDashed"
                            class="text-2xl"></font-awesome-icon>
                        <span class="font-normal text-lg">No results found</span>
                    </template>
                </div>
            </div>

            <div v-else-if="tab === 'selected'">
                <div v-if="filteredSelected.length > 1"
                    @click="deselectAll"
                    tabindex="0"
                    role="button"
                    data-test="select-none-button"
                    class="flex items-center gap-2 pl-4 pb-4">
                    <vx-checkbox :modelValue="true"></vx-checkbox>
                    <span class="text-sm">Select None</span>
                </div>
                <div v-if="filteredSelected.length > 0"
                    class="flex flex-col gap-2">
                    <page-select-item v-for="selectedItem in filteredSelected"
                        :key="'selected_' + props.getOptionKey(selectedItem)"
                        :label="props.getOptionLabel(selectedItem)"
                        @toggle="toggle(selectedItem)"
                        :selected="true">
                        <slot name="option"
                            :item="selectedItem"
                            :selected="true"></slot>
                    </page-select-item>
                </div>
                <div v-else
                    class="flex p-8 items-center justify-center flex-col gap-3">
                    <font-awesome-icon widthAuto :icon="faSquareDashed"
                        class="text-2xl"></font-awesome-icon>
                    <span class="font-normal text-lg">
                        {{ search ? 'No results found' : 'Nothing selected' }}
                    </span>
                </div>
            </div>

        </div>
    </div>
</template>


<script setup>
import { faMagnifyingGlass, faSquareDashed } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxCheckbox, VxIconLoading, VxInput, VxTab, VxTabs } from '@voxie/frontend-components';
import { computed, nextTick, ref, watch } from 'vue';
import PageSelectItem from '~/components/general/PageSelectItem.vue'

const selected = defineModel({
    type: Array,
    required: true,
});

const props = defineProps({
    options: {
        type: Array,
        required: true,
    },
    loading: {
        type: Boolean
    },
    getOptionKey: {
        type: Function,
        default(option) {
            if (typeof option !== 'object') {
                return option;
            }

            return option.id || option.value
        },
    },
    getOptionLabel: {
        type: Function,
        default(option) {
            if (typeof option !== 'object') {
                return option;
            }

            return option.name || option.label
        },
    },
    customSearch: {
        type: Function,
        default: null,
    },
});

const searchEl = ref()
const tab = ref('all') // all|selected
const search = ref('')

watch(tab, () => {
    if (tab.value === 'selected') {
        search.value = '';
    }
    nextTick().then(() => searchEl.value?.field?.focus?.())
}, {
    immediate: true
});

watch(search, (search) => {
    emit('search', search);
})

const emit = defineEmits(['search', 'option:selected', 'option:deselected']);

const searchList = (list) => {
    const searchFor = search.value.trim()
    if (!searchFor.length) {
        return list;
    }

    return list.filter(option => {
        if (props.customSearch) {
            return props.customSearch(option, searchFor);
        }

        const searchForLower = searchFor.toLowerCase();
        const key = String(props.getOptionKey(option) || '').toLowerCase();
        const label = String(props.getOptionLabel(option) || '').toLowerCase();

        return key.includes(searchForLower) || label.includes(searchForLower);
    })
}

const filteredOptions = computed(() => searchList(props.options))
const filteredSelected = computed(() => searchList(selected.value))

const isSelected = (optionToCheck) => {
    return selected.value.some(option => props.getOptionKey(optionToCheck) == props.getOptionKey(option))
}

const toggle = (optionToToggle) => {
    const existingIndex = selected.value.findIndex(option => props.getOptionKey(optionToToggle) == props.getOptionKey(option))
    if (existingIndex !== -1) {
        selected.value.splice(existingIndex, 1)
        emit('option:deselected', optionToToggle)
    } else {
        selected.value.push(optionToToggle);
        emit('option:selected', optionToToggle)
    }
}

const selectAll = computed(() => {
    return filteredOptions.value.every(isSelected)
})

const deselectAll = () => {
    selected.value.forEach(selectedOption => {
        emit('option:deselected', selectedOption);
    })
    selected.value = []
}

const toggleAll = () => {
    if (selectAll.value) {
        deselectAll();
    } else {
        filteredOptions.value.forEach(option => {
            emit('option:selected', option)
        })
        selected.value = [...filteredOptions.value];
    }
}

const handleEnter = () => {
    if (!search.value.length) {
        return;
    }

    if (props.loading) {
        return;
    }

    if (filteredOptions.value[0]) {
        toggle(filteredOptions.value[0]);
    }
};
</script>
